import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faUser, faEnvelope, faShield, faCalendar, faUserPlus, faArchive } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../Api';
import ArchiveUserModal from '../../../components/ArchiveUserModal';

const ChargeEtudeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    status: 'active'
  });
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);

  const roles = {
    'charge_etude': 'Charge d\'Études'
  };

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/users/${id}`);
      setUser(response.data);
      setFormData({
        name: response.data.name,
        status: response.data.status
      });
    } catch (error) {
      console.error('Error fetching user:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      setError(`Erreur lors du chargement du chargé d'études: ${error.response?.data?.message || error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setError('');
      setSuccess('');
      
      // Only send name and status, exclude role
      const updateData = {
        name: formData.name,
        status: formData.status
      };
      
      console.log('Sending update data:', updateData);
      await api.put(`/users/${id}`, updateData);
      setSuccess('Charge d\'études mise à jour avec succès');
      setIsEditing(false);
      fetchUser(); // Refresh user data
    } catch (error) {
      console.error('Error updating user:', error);
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Erreur lors de la mise à jour de la charge d\'études');
      }
    }
  };

  const handleArchive = () => {
    setArchiveModalOpen(true);
  };

  const handleArchiveSuccess = () => {
    navigate('/charge-etudes');
  };

  const canModifyUser = () => {
    if (!user || !currentUser) return false;
    // Chargé d'études supérieur can modify chargé d'études
    return currentUser.role === 'charge_etude_superieur';
  };

  const canArchiveUser = () => {
    if (!user || !currentUser) return false;
    // Chargé d'études supérieur can archive chargé d'études
    return currentUser.role === 'charge_etude_superieur';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'suspended': return 'text-red-600 bg-red-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'suspended': return 'Suspendu';
      case 'inactive': return 'Inactif (Automatique)';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden flex items-center justify-center">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du chargé d'études...</p>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden flex items-center justify-center">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Erreur</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/charge-etudes')}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retour aux chargés d'études
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 flex justify-between items-center p-6 animate-fade-in">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/charge-etudes')}
            className="mr-4 p-2 text-gray-600 hover:text-red-600 transition-colors duration-300 ease-in-out"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-gray-800">
            DÉTAILS DU CHARGÉ D'ÉTUDES
          </h1>
        </div>
        <div className="flex items-center space-x-4">
          {canModifyUser() && (
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FontAwesomeIcon icon={faSave} className="mr-2" />
              {isEditing ? 'Annuler' : 'Modifier'}
            </button>
          )}
          {canArchiveUser() && (
            <button
              onClick={handleArchive}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <FontAwesomeIcon icon={faArchive} className="mr-2" />
              Archiver
            </button>
          )}
        </div>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-6 animate-slide-up">
        <div className="max-w-4xl mx-auto">
          {/* Success/Error Messages */}
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-600">{success}</p>
            </div>
          )}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {/* User Information */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Basic Information */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Informations de base
                </h3>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faUser} className="mr-2" />
                    Nom complet
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{user.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                    Adresse email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={user.email}
                    disabled={true}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 bg-gray-50 rounded-lg text-gray-500 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    L'adresse email ne peut pas être modifiée
                  </p>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faShield} className="mr-2" />
                    Rôle
                  </label>
                  <p className="text-gray-900 font-medium">{roles[user.role] || user.role}</p>
                  {isEditing && (
                    <p className="text-xs text-gray-500 mt-1">Le rôle ne peut pas être modifié</p>
                  )}
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut
                  </label>
                  {isEditing ? (
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                    >
                      <option value="active">Actif</option>
                      <option value="suspended">Suspendu</option>
                    </select>
                  ) : (
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(user.status)}`}>
                      {getStatusText(user.status)}
                    </span>
                  )}
                  {isEditing && (
                    <p className="text-xs text-gray-500 mt-1">Seul l'administrateur général peut suspendre des responsables</p>
                  )}
                </div>
              </div>

              {/* Metadata Information */}
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Informations système
                </h3>

                {/* User ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ID Chargé d'Études
                  </label>
                  <p className="text-gray-500 font-mono text-sm">{user.id}</p>
                </div>

                {/* Created By */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                    Créé par
                  </label>
                  <p className="text-gray-900">{user.created_by || 'Système'}</p>
                </div>

                {/* Last Login */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                    Dernière connexion
                  </label>
                  <p className="text-gray-900">{formatDate(user.last_login_at)}</p>
                </div>

                {/* Created At */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                    Date de création
                  </label>
                  <p className="text-gray-900">{formatDate(user.created_at)}</p>
                </div>
              </div>
            </div>

            {/* Save Button */}
            {isEditing && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={handleSave}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
                >
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                  Sauvegarder
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Archive Modal */}
      <ArchiveUserModal
        isOpen={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        user={user}
        onArchive={handleArchiveSuccess}
      />
    </div>
  );
};

export default ChargeEtudeDetail;
